import { useState, useEffect } from "react";
import { Download, X, Share, Plus } from "lucide-react";

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const isInStandaloneMode = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showAndroid, setShowAndroid] = useState(false);
  const [showIOS, setShowIOS] = useState(false);

  useEffect(() => {
    // Don't show if already installed
    if (isInStandaloneMode()) return;
    // Don't show if dismissed in this session
    if (sessionStorage.getItem('pwa-dismissed')) return;

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowAndroid(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Show iOS guide after 8 seconds on mobile Safari
    let iosTimer;
    if (isIOS() && !sessionStorage.getItem('pwa-dismissed')) {
      iosTimer = setTimeout(() => setShowIOS(true), 8000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowAndroid(false);
    }
    setDeferredPrompt(null);
  };

  const dismiss = () => {
    setShowAndroid(false);
    setShowIOS(false);
    sessionStorage.setItem('pwa-dismissed', '1');
  };

  if (!showAndroid && !showIOS) return null;

  return (
    <div
      className="fixed bottom-20 left-4 right-4 z-40 md:left-auto md:right-6 md:w-80"
      data-testid="pwa-install-banner"
    >
      {/* Android / Desktop install prompt */}
      {showAndroid && (
        <div className="bg-[#111] border border-[#D62828]/40 rounded-sm shadow-2xl overflow-hidden">
          <div className="bg-[#D62828] px-4 py-2 flex items-center justify-between">
            <span className="text-white text-xs font-bold uppercase tracking-wider">Install App</span>
            <button onClick={dismiss} className="text-white/70 hover:text-white" aria-label="Dismiss">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="px-4 py-3 flex items-start gap-3">
            <img src="/icons/icon-192.png" alt="Cut Above" className="w-12 h-12 rounded-sm flex-shrink-0" />
            <div>
              <p className="text-white text-sm font-semibold">Cut Above Polish Shop</p>
              <p className="text-gray-400 text-xs mt-0.5 leading-relaxed">
                Add to your home screen for fast 1-tap ordering
              </p>
            </div>
          </div>
          <div className="px-4 pb-4 flex gap-3">
            <button
              onClick={handleInstall}
              data-testid="pwa-install-btn"
              className="flex-1 flex items-center justify-center gap-2 bg-[#D62828] hover:bg-[#B91C1C] text-white text-xs font-bold uppercase tracking-wider py-2.5 rounded-sm transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              Install Free
            </button>
            <button onClick={dismiss} className="px-4 text-gray-500 hover:text-gray-300 text-xs">
              Not now
            </button>
          </div>
        </div>
      )}

      {/* iOS Safari instructions */}
      {showIOS && (
        <div className="bg-[#111] border border-white/10 rounded-sm shadow-2xl overflow-hidden">
          <div className="bg-[#1A1A1A] px-4 py-2 flex items-center justify-between border-b border-white/10">
            <span className="text-white text-xs font-bold uppercase tracking-wider">Add to Home Screen</span>
            <button onClick={dismiss} className="text-gray-500 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="px-4 py-4">
            <p className="text-gray-300 text-xs leading-relaxed mb-3">
              Install Cut Above for quick 1-tap ordering:
            </p>
            <ol className="space-y-2.5">
              <li className="flex items-center gap-2.5 text-xs text-gray-400">
                <span className="w-5 h-5 bg-[#D62828] rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 text-[10px]">1</span>
                Tap <Share className="w-3.5 h-3.5 text-blue-400 inline mx-1" /> Share in Safari's toolbar
              </li>
              <li className="flex items-center gap-2.5 text-xs text-gray-400">
                <span className="w-5 h-5 bg-[#D62828] rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 text-[10px]">2</span>
                Tap <Plus className="w-3.5 h-3.5 text-gray-300 inline mx-1" /> <span className="text-gray-300">"Add to Home Screen"</span>
              </li>
              <li className="flex items-center gap-2.5 text-xs text-gray-400">
                <span className="w-5 h-5 bg-[#D62828] rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 text-[10px]">3</span>
                Tap <span className="text-[#D4AF37] font-semibold">"Add"</span> — done!
              </li>
            </ol>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstallPrompt;
